import { FolderIcon } from '@heroicons/react/24/outline';
import HubCard from '../../components/hubs/HubCard';
import { GROUPING } from '../../branding/grouping';
import { Hub } from '../../types/hub';
import { exampleOptions } from './cardReviewStorage';
import { hubScent } from './scentViews';
import { ExampleSwitcher, FamilyBlock } from './reviewUi';

function DescriptionTile({ hub }: { hub: Hub }) {
  return (
    <div className="min-h-[44px] p-4 bg-white border border-gray-200 rounded-lg">
      <FolderIcon className="h-6 w-6 text-primary mb-2" aria-hidden />
      <span className="block text-sm font-medium text-gray-900">{hub.title}</span>
      {hub.description && <span className="block text-xs text-gray-500 mt-1 line-clamp-2">{hub.description}</span>}
    </div>
  );
}

function NameChip({ hub }: { hub: Hub }) {
  const label = hub.program ? `${hub.program.title} · ${hub.title}` : hub.title;
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <FolderIcon className="h-5 w-5 text-primary" aria-hidden />
      <span className="text-sm font-medium text-gray-800">{label}</span>
    </div>
  );
}

function SearchRow({ hub }: { hub: Hub }) {
  return (
    <div className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg">
      <FolderIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden />
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-0.5">{GROUPING.singular}</p>
        <h4 className="font-semibold text-gray-900 mb-1 line-clamp-1">{hub.title}</h4>
        {hub.description && <p className="text-sm text-gray-600 line-clamp-2">{hub.description}</p>}
      </div>
    </div>
  );
}

function PublicHomeTile({ hub }: { hub: Hub }) {
  return (
    <div className="flex flex-col items-center p-8 bg-white border border-gray-200 rounded-xl shadow-sm">
      <FolderIcon className="h-12 w-12 text-primary mb-4" aria-hidden />
      <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">{hub.title}</h3>
      {hub.description && <p className="text-sm text-gray-600 text-center line-clamp-3">{hub.description}</p>}
    </div>
  );
}

export default function HubReview({
  hub,
  programHub,
  notice,
  error,
  choices,
  onChoose,
  notes,
  onNote,
}: {
  hub: Hub | null;
  programHub: Hub | null;
  notice: string | null;
  error: string | null;
  choices: Record<string, string>;
  onChoose: (exampleId: string, optionId: string) => void;
  notes: Record<string, string>;
  onNote: (exampleId: string, note: string) => void;
}) {
  const stripHub = programHub ?? hub;
  return (
    <FamilyBlock
      id="review-hubs"
      title="Hubs"
      judge="Type rail draws a hub as a folder: a tab with the resource count, the hub name below it, and save. The other options stay specific to this place."
      destination="A hub card opens the hub place."
      notice={notice}
      error={error}
    >
      {hub && (
        <>
          <ExampleSwitcher
            exampleId="hubs-index"
            label="Programs index and search"
            options={exampleOptions('hubs-index')}
            choice={choices['hubs-index'] ?? ''}
            onChoose={(optionId) => onChoose('hubs-index', optionId)}
            note={notes['hubs-index'] ?? ''}
            onNote={(value) => onNote('hubs-index', value)}
            render={(optionId, frame) =>
              optionId === 'current' ? (
                <div className={frame === 'desktop' ? 'max-w-xs' : undefined}>
                  <HubCard hub={hub} />
                </div>
              ) : (
                hubScent(hub, optionId, frame)
              )
            }
          />
          {programHub && (
            <ExampleSwitcher
              exampleId="hubs-program"
              label="Search, program hub"
              options={exampleOptions('hubs-program')}
              choice={choices['hubs-program'] ?? ''}
              onChoose={(optionId) => onChoose('hubs-program', optionId)}
              note={notes['hubs-program'] ?? ''}
              onNote={(value) => onNote('hubs-program', value)}
              render={(optionId, frame) =>
                optionId === 'current' ? (
                  <div className={frame === 'desktop' ? 'max-w-xs' : undefined}>
                    <HubCard hub={programHub} />
                  </div>
                ) : (
                  hubScent(programHub, optionId, frame)
                )
              }
            />
          )}
          <ExampleSwitcher
            exampleId="hubs-practice"
            label="Practice home"
            options={exampleOptions('hubs-practice')}
            choice={choices['hubs-practice'] ?? ''}
            onChoose={(optionId) => onChoose('hubs-practice', optionId)}
            note={notes['hubs-practice'] ?? ''}
            onNote={(value) => onNote('hubs-practice', value)}
            render={(optionId, frame) =>
              optionId === 'current' ? (
                <div className={frame === 'desktop' ? 'max-w-xs' : undefined}>
                  <DescriptionTile hub={hub} />
                </div>
              ) : (
                hubScent(hub, optionId, frame)
              )
            }
          />
          <ExampleSwitcher
            exampleId="hubs-home"
            label="Public home"
            options={exampleOptions('hubs-home')}
            choice={choices['hubs-home'] ?? ''}
            onChoose={(optionId) => onChoose('hubs-home', optionId)}
            note={notes['hubs-home'] ?? ''}
            onNote={(value) => onNote('hubs-home', value)}
            render={(optionId, frame) =>
              optionId === 'current' ? (
                <div className={frame === 'desktop' ? 'max-w-sm' : undefined}>
                  <PublicHomeTile hub={hub} />
                </div>
              ) : (
                hubScent(hub, optionId, frame)
              )
            }
          />
          {stripHub && (
            <ExampleSwitcher
              exampleId="hubs-library"
              label={programHub ? 'Library strip, program hub' : 'Library strip'}
              options={exampleOptions('hubs-library')}
              choice={choices['hubs-library'] ?? ''}
              onChoose={(optionId) => onChoose('hubs-library', optionId)}
              note={notes['hubs-library'] ?? ''}
              onNote={(value) => onNote('hubs-library', value)}
              render={(optionId) => (optionId === 'current' ? <NameChip hub={stripHub} /> : hubScent(stripHub, optionId, "desktop"))}
            />
          )}
          <ExampleSwitcher
            exampleId="hubs-search"
            label="Header search"
            options={exampleOptions('hubs-search')}
            choice={choices['hubs-search'] ?? ''}
            onChoose={(optionId) => onChoose('hubs-search', optionId)}
            note={notes['hubs-search'] ?? ''}
            onNote={(value) => onNote('hubs-search', value)}
            render={(optionId, frame) => {
              if (optionId === 'current') return <SearchRow hub={hub} />;
              const specimen = optionId.startsWith('modern-') && programHub ? programHub : hub;
              return hubScent(specimen, optionId, frame, optionId.startsWith('modern-') ? 'search' : undefined);
            }}
          />
        </>
      )}
    </FamilyBlock>
  );
}
